import React, { useState, useEffect } from 'react';
import { AuthContext, API, BACKEND_URL } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FaSignOutAlt, FaPlus, FaTrash, FaUsers, FaDumbbell, FaUtensils, FaCheck, FaChartLine } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const TrainerDashboard = () => {
  const { user, logout } = React.useContext(AuthContext);
  const token = localStorage.getItem('token');
  const [clients, setClients] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [showAssignWorkout, setShowAssignWorkout] = useState(false);
  const [showSetNutrition, setShowSetNutrition] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [showClientStats, setShowClientStats] = useState(false);
  const [clientMeasurements, setClientMeasurements] = useState([]);
  const [clientWorkouts, setClientWorkouts] = useState([]);
  const [selectedDayForView, setSelectedDayForView] = useState(1);
  const [showEditExercise, setShowEditExercise] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showMealPlan, setShowMealPlan] = useState(false);
  const [mealPlans, setMealPlans] = useState([]);
  const [selectedMealDay, setSelectedMealDay] = useState(1);
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('all');
  const [editingMealPlan, setEditingMealPlan] = useState(null);
  const [exerciseTypes, setExerciseTypes] = useState({}); // Track exercise type per exercise {exerciseId: 'reps' or 'time'}

  useEffect(() => {
    fetchClients();
    fetchExercises();
  }, []);

  useEffect(() => {
    if (selectedClient && showAssignWorkout) {
      fetchClientWorkouts();
    }
  }, [selectedClient, showAssignWorkout]);

  useEffect(() => {
    if (selectedClient && showMealPlan) {
      fetchMealPlans();
    }
  }, [selectedClient, showMealPlan]);

  const fetchClientWorkouts = async () => {
    if (!selectedClient) return;
    try {
      const response = await axios.get(`${API}/workouts/client/${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientWorkouts(response.data);
    } catch (error) {
      console.error('Error fetching client workouts:', error);
      setClientWorkouts([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExercises(response.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const handleAddExercise = async (e) => {
    e.preventDefault();
    setUploadingVideo(true);
    const formData = new FormData(e.target);
    
    try {
      await axios.post(`${API}/exercises`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Vežba dodata!');
      setShowAddExercise(false);
      fetchExercises();
      e.target.reset();
    } catch (error) {
      toast.error('Greška pri dodavanju vežbe');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleEditExercise = async (e) => {
    e.preventDefault();
    setUploadingVideo(true);
    const formData = new FormData(e.target);
    
    // Add exercise ID to form data
    formData.append('exercise_id', editingExercise.id);
    
    try {
      await axios.put(`${API}/exercises/${editingExercise.id}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Vežba ažurirana!');
      setShowEditExercise(false);
      setEditingExercise(null);
      fetchExercises();
    } catch (error) {
      toast.error('Greška pri ažuriranju vežbe');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleDeleteExercise = async (id) => {
    if (!confirm('Da li ste sigurni da želite da obrišete ovu vežbu?')) return;
    
    try {
      await axios.delete(`${API}/exercises/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Vežba obrisana!');
      fetchExercises();
    } catch (error) {
      toast.error('Greška pri brisanju vežbe');
    }
  };

  const handleMarkAsPaid = async (clientId) => {
    try {
      await axios.put(`${API}/clients/${clientId}/payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Klijent označen kao plaćen!');
      fetchClients();
    } catch (error) {
      toast.error('Greška pri označavanju plaćanja');
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!confirm(`Da li ste sigurni da želite da obrišete klijenta ${clientName}? Ova akcija će obrisati sve njegove podatke i ne može se poništiti.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Klijent uspešno obrisan!');
      fetchClients();
    } catch (error) {
      toast.error('Greška pri brisanju klijenta');
    }
  };

  const handleAssignWorkout = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const day = parseInt(formData.get('day'));
    
    // Validate that all exercises have been selected
    if (selectedExercises.some(ex => !ex.exercise_id)) {
      toast.error('Molimo izaberite sve vežbe');
      return;
    }
    
    // Filter out exercises with empty sets
    const exercisesData = selectedExercises
      .filter(ex => ex.exercise_id && ex.sets.length > 0)
      .map(ex => ({
        exercise_id: ex.exercise_id,
        sets: ex.sets
      }));
    
    if (exercisesData.length === 0) {
      toast.error('Dodajte najmanje jednu vežbu');
      return;
    }
    
    try {
      await axios.post(`${API}/workouts/assign`, {
        client_id: selectedClient.id,
        day: day,
        exercises: exercisesData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Trening dodeljen!');
      setShowAssignWorkout(false);
      setSelectedExercises([]);
      fetchClientWorkouts(); // Refresh workouts to show in overview
    } catch (error) {
      toast.error('Greška pri dodeli treninga');
    }
  };

  const handleSetNutrition = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const day = parseInt(formData.get('day'));
    
    try {
      await axios.post(`${API}/nutrition/goals`, {
        client_id: selectedClient.id,
        day: day,
        calories: parseFloat(formData.get('calories')),
        protein: parseFloat(formData.get('protein')),
        fats: parseFloat(formData.get('fats')),
        carbs: parseFloat(formData.get('carbs'))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Nutritivni ciljevi postavljeni!');
      setShowSetNutrition(false);
    } catch (error) {
      toast.error('Greška pri postavljanju ciljeva');
    }
  };

  const fetchMealPlans = async () => {
    if (!selectedClient) return;
    try {
      const response = await axios.get(`${API}/meal-plans/${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMealPlans(response.data);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
      setMealPlans([]);
    }
  };

  const handleCreateMealPlan = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const meals = [];
    const mealTypes = ['dorucak', 'rucak', 'vecera', 'uzina'];
    
    mealTypes.forEach(type => {
      const description = formData.get(type);
      if (description && description.trim()) {
        meals.push({
          meal_type: type,
          description: description.trim()
        });
      }
    });
    
    if (meals.length === 0) {
      toast.error('Dodajte bar jedan obrok');
      return;
    }
    
    try {
      if (editingMealPlan) {
        // Update existing meal plan
        await axios.put(`${API}/meal-plans/${editingMealPlan.id}`, {
          client_id: selectedClient.id,
          day: parseInt(formData.get('day')),
          meals: meals
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan obroka ažuriran!');
        setEditingMealPlan(null);
      } else {
        // Create new meal plan
        await axios.post(`${API}/meal-plans`, {
          client_id: selectedClient.id,
          day: parseInt(formData.get('day')),
          meals: meals
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan obroka kreiran!');
      }
      
      fetchMealPlans();
      e.target.reset();
    } catch (error) {
      toast.error(editingMealPlan ? 'Greška pri ažuriranju plana obroka' : 'Greška pri kreiranju plana obroka');
    }
  };

  const handleDeleteMealPlan = async (planId) => {
    if (!confirm('Da li ste sigurni da želite da obrišete ovaj plan obroka?')) {
      return;
    }

    try {
      await axios.delete(`${API}/meal-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan obroka obrisan!');
      fetchMealPlans();
    } catch (error) {
      toast.error('Greška pri brisanju plana obroka');
    }
  };

  const filteredClients = clients.filter(c => 
    c.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-black text-white" data-testid="trainer-dashboard">
      {/* Header */}
      <header className="glass-dark border-b border-gray-800 sticky top-0 z-50 animate-fade-in">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src="/logo.png" 
                alt="ReStart Fit Logo" 
                className="h-10 sm:h-12 animate-scale-in"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg sm:text-xl font-bold text-white">Trener Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-400">Dobrodošao, {user.first_name}!</p>
              </div>
            </div>
            <Button 
              onClick={logout} 
              variant="outline" 
              className="border-gray-700 text-white text-xs sm:text-base hover:scale-105 transition-transform" 
              size="sm"
              data-testid="logout-btn"
            >
              <FaSignOutAlt className="sm:mr-2" /> <span className="hidden sm:inline">Odjava</span>
            </Button>
          </div>
          <div className="sm:hidden mt-2">
            <p className="text-sm font-semibold text-white">Trener Dashboard</p>
            <p className="text-xs text-gray-400">Dobrodošao, {user.first_name}!</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <Tabs defaultValue="klijenti" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-gray-900 mb-4 sm:mb-8 p-1 animate-slide-in-up">
            <TabsTrigger 
              value="klijenti" 
              data-testid="clients-tab"
              className="text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 py-2 transition-all hover:scale-105"
            >
              <FaUsers className="text-sm sm:text-base" /> 
              <span>Klijenti</span>
            </TabsTrigger>
            <TabsTrigger 
              value="vezbe" 
              data-testid="exercises-tab"
              className="text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 py-2 transition-all hover:scale-105"
            >
              <FaDumbbell className="text-sm sm:text-base" /> 
              <span>Vežbe</span>
            </TabsTrigger>
            <TabsTrigger 
              value="dodele" 
              data-testid="assignments-tab"
              className="text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 py-2 transition-all hover:scale-105"
            >
              <FaUtensils className="text-sm sm:text-base" /> 
              <span>Dodele</span>
            </TabsTrigger>
          </TabsList>

          {/* Klijenti Tab */}
          <TabsContent value="klijenti">
            <div className="mb-6">
              <Input
                placeholder="Pretraži klijente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-400"
                data-testid="search-clients-input"
              />
            </div>

            <div className="grid gap-4">
              {filteredClients.map(client => {
                const isPaid = client.is_paid && client.payment_expiry && new Date(client.payment_expiry) > new Date();
                
                return (
                  <Card key={client.id} className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 animate-slide-in-up hover:scale-[1.01] transition-transform duration-300" data-testid={`client-card-${client.id}`}>
                    <CardContent className="py-6">
                      <div className="flex flex-col gap-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold text-white">{client.first_name} {client.last_name}</h3>
                            <p className="text-base text-gray-300">{client.email}</p>
                            <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:text-base">
                              <div>
                                <span className="text-gray-300 font-medium">Visina:</span> <span className="text-white">{client.height} cm</span>
                              </div>
                              <div>
                                <span className="text-gray-300 font-medium">Težina:</span> <span className="text-white">{client.weight} kg</span>
                              </div>
                              <div>
                                <span className="text-gray-300 font-medium">Pol:</span> <span className="text-white">{client.gender === 'male' ? 'Muškarac' : 'Žena'}</span>
                              </div>
                              <div>
                                <span className="text-gray-300 font-medium">Status:</span>{' '}
                                <span className={`${isPaid ? 'accent-text' : 'text-red-400'} font-bold text-sm sm:text-base`}>
                                  {isPaid ? 'Plaćeno' : 'Nije plaćeno'}
                                </span>
                              </div>
                            </div>
                            {client.previous_injuries && (
                              <div className="mt-3">
                                <span className="text-gray-300 text-base font-medium">Povrede:</span>
                                <p className="text-base text-white mt-1">{client.previous_injuries}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Quick Actions */}
                        <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-700">
                          <Button
                            onClick={() => {
                              setSelectedClient(client);
                              setShowAssignWorkout(true);
                            }}
                            className="accent-bg text-black font-semibold hover:opacity-90 text-xs sm:text-sm whitespace-nowrap"
                            size="sm"
                            data-testid={`quick-assign-workout-${client.id}`}
                          >
                            <FaDumbbell className="mr-1 flex-shrink-0" /> 
                            <span className="truncate">Dodaj trening</span>
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedClient(client);
                              setShowMealPlan(true);
                            }}
                            className="accent-bg text-black font-semibold hover:opacity-90 text-xs sm:text-sm whitespace-nowrap"
                            size="sm"
                            data-testid={`quick-set-meal-plan-${client.id}`}
                          >
                            <FaUtensils className="mr-1 flex-shrink-0" /> 
                            <span className="truncate">Plan ishrane</span>
                          </Button>
                          <Button
                            onClick={async () => {
                              setSelectedClient(client);
                              // Fetch client measurements
                              try {
                                const response = await axios.get(`${API}/measurements/${client.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setClientMeasurements(response.data);
                                setShowClientStats(true);
                              } catch (error) {
                                toast.error('Greška pri učitavanju statistike');
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs sm:text-sm whitespace-nowrap"
                            size="sm"
                            data-testid={`view-stats-${client.id}`}
                          >
                            <FaChartLine className="mr-1 flex-shrink-0" /> 
                            <span className="truncate">Statistika</span>
                          </Button>
                          {!isPaid && (
                            <Button
                              onClick={() => handleMarkAsPaid(client.id)}
                              className="bg-green-600 hover:bg-green-700 text-white font-semibold text-xs sm:text-sm whitespace-nowrap"
                              size="sm"
                              data-testid={`mark-paid-${client.id}`}
                            >
                              <FaCheck className="mr-1 flex-shrink-0" /> 
                              <span className="truncate">Označi plaćen</span>
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteClient(client.id, `${client.first_name} ${client.last_name}`)}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold text-xs sm:text-sm whitespace-nowrap"
                            size="sm"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <FaTrash className="mr-1 flex-shrink-0" /> 
                            <span className="truncate">Obriši</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Vežbe Tab */}
          <TabsContent value="vezbe">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Sve vežbe</h2>
              <Button 
                onClick={() => setShowAddExercise(true)} 
                className="accent-bg text-black font-bold w-full sm:w-auto text-sm sm:text-base" 
                size="sm"
                data-testid="add-exercise-btn"
              >
                <FaPlus className="mr-1 sm:mr-2" /> Dodaj vežbu
              </Button>
            </div>

            {/* Search and Filter */}
            <div className="mb-6 space-y-3">
              <Input
                type="text"
                placeholder="Pretraži vežbe po nazivu..."
                value={exerciseSearchTerm}
                onChange={(e) => setExerciseSearchTerm(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="exercise-search-input"
              />
              
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedMuscleGroup('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedMuscleGroup === 'all'
                      ? 'accent-bg text-black'
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                >
                  Sve ({exercises.length})
                </button>
                {Array.from(new Set(exercises.map(ex => ex.muscle_group))).sort().map(group => {
                  const count = exercises.filter(ex => ex.muscle_group === group).length;
                  return (
                    <button
                      key={group}
                      onClick={() => setSelectedMuscleGroup(group)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedMuscleGroup === group
                          ? 'accent-bg text-black'
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      {group} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4">
              {(() => {
                const filteredExercises = exercises.filter(exercise => {
                  // Filter by search term
                  const matchesSearch = exercise.name.toLowerCase().includes(exerciseSearchTerm.toLowerCase()) ||
                                       exercise.description.toLowerCase().includes(exerciseSearchTerm.toLowerCase());
                  
                  // Filter by muscle group
                  const matchesMuscleGroup = selectedMuscleGroup === 'all' || exercise.muscle_group === selectedMuscleGroup;
                  
                  return matchesSearch && matchesMuscleGroup;
                });

                if (filteredExercises.length === 0) {
                  return (
                    <Card className="bg-gray-800 border-gray-700">
                      <CardContent className="py-12 text-center">
                        <FaDumbbell className="mx-auto text-gray-600 text-5xl mb-4" />
                        <p className="text-gray-400 text-lg">
                          {exerciseSearchTerm || selectedMuscleGroup !== 'all' 
                            ? 'Nema vežbi koje odgovaraju kriterijumima pretrage'
                            : 'Još niste dodali vežbe'}
                        </p>
                        {(exerciseSearchTerm || selectedMuscleGroup !== 'all') && (
                          <Button
                            onClick={() => {
                              setExerciseSearchTerm('');
                              setSelectedMuscleGroup('all');
                            }}
                            className="mt-4 bg-gray-700 hover:bg-gray-600 text-white"
                          >
                            Resetuj filtere
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                }

                return filteredExercises.map(exercise => (
                  <Card key={exercise.id} className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 animate-slide-in-up hover:scale-[1.01] transition-transform duration-300" data-testid={`exercise-card-${exercise.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-white text-xl">{exercise.name}</CardTitle>
                          <CardDescription className="text-gray-300 text-base">{exercise.muscle_group}</CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              setEditingExercise(exercise);
                              setShowEditExercise(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            size="sm"
                            data-testid={`edit-exercise-${exercise.id}`}
                          >
                            Uredi
                          </Button>
                          <Button
                            onClick={() => handleDeleteExercise(exercise.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            size="sm"
                            data-testid={`delete-exercise-${exercise.id}`}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-200 text-base">{exercise.description}</p>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          </TabsContent>

          {/* Dodele Tab */}
          <TabsContent value="dodele">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-xl">Dodeli trening</CardTitle>
                  <CardDescription className="text-gray-300">Dodeli vežbe klijentu za određeni dan</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-200">Izaberi klijenta</Label>
                      <select
                        className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white"
                        onChange={(e) => {
                          const client = clients.find(c => c.id === e.target.value);
                          setSelectedClient(client);
                        }}
                        data-testid="select-client-workout"
                      >
                        <option value="">-- Izaberi --</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      onClick={() => setShowAssignWorkout(true)}
                      disabled={!selectedClient}
                      className="w-full accent-bg text-black font-bold disabled:opacity-50"
                      data-testid="open-assign-workout-btn"
                    >
                      Dodeli vežbe
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-xl">Postavi nutritivne ciljeve</CardTitle>
                  <CardDescription className="text-gray-300">Definiši dnevne makroe za klijenta</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-200">Izaberi klijenta</Label>
                      <select
                        className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white"
                        onChange={(e) => {
                          const client = clients.find(c => c.id === e.target.value);
                          setSelectedClient(client);
                        }}
                        data-testid="select-client-nutrition"
                      >
                        <option value="">-- Izaberi --</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      onClick={() => setShowSetNutrition(true)}
                      disabled={!selectedClient}
                      className="w-full accent-bg text-black font-bold disabled:opacity-50"
                      data-testid="open-set-nutrition-btn"
                    >
                      Postavi ciljeve
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Exercise Dialog */}
      <Dialog open={showAddExercise} onOpenChange={setShowAddExercise}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl" data-testid="add-exercise-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Dodaj novu vežbu</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddExercise} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-gray-200">Naziv vežbe</Label>
              <Input id="name" name="name" required className="bg-gray-800 border-gray-600 text-white" data-testid="exercise-name-input" />
            </div>
            <div>
              <Label htmlFor="muscle_group" className="text-gray-200">Mišićna grupa</Label>
              <Input id="muscle_group" name="muscle_group" required className="bg-gray-800 border-gray-600 text-white" data-testid="exercise-muscle-input" />
            </div>
            <div>
              <Label htmlFor="description" className="text-gray-200">Opis</Label>
              <Textarea id="description" name="description" required className="bg-gray-800 border-gray-600 text-white" rows={4} data-testid="exercise-description-input" />
            </div>
            <div>
              <Label htmlFor="youtube_url" className="text-gray-200">YouTube Link</Label>
              <Input 
                id="youtube_url" 
                name="youtube_url" 
                type="text" 
                required 
                placeholder="https://www.youtube.com/watch?v=... ili https://youtu.be/..."
                className="bg-gray-800 border-gray-600 text-white" 
                data-testid="exercise-youtube-input" 
              />
              <p className="text-gray-400 text-sm mt-1">Unesite pun YouTube link ili samo video ID</p>
            </div>
            <Button type="submit" disabled={uploadingVideo} className="w-full accent-bg text-black font-bold" data-testid="submit-exercise-btn">
              {uploadingVideo ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  Čuvanje...
                </span>
              ) : 'Dodaj vežbu'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Exercise Dialog */}
      <Dialog open={showEditExercise} onOpenChange={setShowEditExercise}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-exercise-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Uredi vežbu</DialogTitle>
          </DialogHeader>
          {editingExercise && (
            <>
              {/* Show current video */}
              {editingExercise.youtube_url && (
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <h3 className="text-white font-semibold mb-3">Trenutni video:</h3>
                  <div className="relative rounded-lg overflow-hidden" style={{paddingBottom: '56.25%'}}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src={`https://www.youtube.com/embed/${editingExercise.youtube_url.includes('youtu.be') ? editingExercise.youtube_url.split('youtu.be/')[1]?.split('?')[0] : editingExercise.youtube_url.includes('watch?v=') ? editingExercise.youtube_url.split('watch?v=')[1]?.split('&')[0] : editingExercise.youtube_url}`}
                      title={editingExercise.name}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleEditExercise} className="space-y-4">
                <div>
                  <Label htmlFor="edit-name" className="text-gray-200">Naziv vežbe</Label>
                  <Input 
                    id="edit-name" 
                    name="name" 
                    required 
                    defaultValue={editingExercise.name}
                    className="bg-gray-800 border-gray-600 text-white" 
                    data-testid="edit-exercise-name-input" 
                  />
                </div>
                <div>
                  <Label htmlFor="edit-muscle_group" className="text-gray-200">Mišićna grupa</Label>
                  <Input 
                    id="edit-muscle_group" 
                    name="muscle_group" 
                    required 
                    defaultValue={editingExercise.muscle_group}
                    className="bg-gray-800 border-gray-600 text-white" 
                    data-testid="edit-exercise-muscle-input" 
                  />
                </div>
                <div>
                  <Label htmlFor="edit-description" className="text-gray-200">Opis</Label>
                  <Textarea 
                    id="edit-description" 
                    name="description" 
                    required 
                    defaultValue={editingExercise.description}
                    className="bg-gray-800 border-gray-600 text-white" 
                    rows={4} 
                    data-testid="edit-exercise-description-input" 
                  />
                </div>
                <div>
                  <Label htmlFor="edit-youtube_url" className="text-gray-200">YouTube Link (opcionalno - ostavite prazno ako ne želite da promenite)</Label>
                  <Input 
                    id="edit-youtube_url" 
                    name="youtube_url" 
                    type="text" 
                    placeholder="https://www.youtube.com/watch?v=... ili https://youtu.be/..."
                    defaultValue={editingExercise.youtube_url}
                    className="bg-gray-800 border-gray-600 text-white" 
                    data-testid="edit-exercise-youtube-input" 
                  />
                  <p className="text-gray-400 text-sm mt-1">Unesite pun YouTube link ili samo video ID</p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button"
                    onClick={() => {
                      setShowEditExercise(false);
                      setEditingExercise(null);
                    }}
                    variant="outline"
                    className="flex-1 border-gray-600 text-white"
                  >
                    Otkaži
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={uploadingVideo} 
                    className="flex-1 accent-bg text-black font-bold" 
                    data-testid="submit-edit-exercise-btn"
                  >
                    {uploadingVideo ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                        Ažuriranje...
                      </span>
                    ) : 'Sačuvaj izmene'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Workout Dialog */}
      <Dialog open={showAssignWorkout} onOpenChange={(open) => {
        setShowAssignWorkout(open);
        if (!open) {
          setSelectedExercises([]);
          setSelectedDayForView(1);
        } else {
          // Initialize with empty array for new workouts
          setSelectedExercises([]);
        }
      }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="assign-workout-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Dodeli trening - {selectedClient?.first_name} {selectedClient?.last_name}</DialogTitle>
          </DialogHeader>

          {/* Current Workouts Overview */}
          <div className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-700">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <FaDumbbell /> Trenutni treninzi po danima
            </h3>
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map(day => {
                const dayWorkout = clientWorkouts.find(w => w.day === day);
                const hasWorkout = dayWorkout && dayWorkout.exercises.length > 0;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDayForView(day)}
                    className={`p-3 rounded-lg text-center transition-all ${
                      selectedDayForView === day 
                        ? 'accent-bg text-black font-bold' 
                        : hasWorkout 
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <div className="text-xs mb-1">Dan {day}</div>
                    <div className="text-lg font-bold">{hasWorkout ? dayWorkout.exercises.length : 0}</div>
                    <div className="text-xs">{hasWorkout ? 'vežbi' : 'prazno'}</div>
                  </button>
                );
              })}
            </div>
            
            {/* Show exercises for selected day */}
            {(() => {
              const dayWorkout = clientWorkouts.find(w => w.day === selectedDayForView);
              if (dayWorkout && dayWorkout.exercises.length > 0) {
                return (
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-white font-medium">Vežbe za Dan {selectedDayForView}:</h4>
                      <Button
                        type="button"
                        onClick={() => {
                          // Load existing workout into form for editing
                          const exercisesToEdit = dayWorkout.exercises.map(ex => ({
                            exercise_id: ex.exercise_id,
                            sets: ex.sets
                          }));
                          setSelectedExercises(exercisesToEdit);
                          // Set the day selector to current day
                          document.getElementById('day').value = selectedDayForView;
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                        size="sm"
                      >
                        <FaDumbbell className="mr-1" /> Uredi trening
                      </Button>
                    </div>
                    {dayWorkout.exercises.map((ex, idx) => {
                      const exercise = exercises.find(e => e.id === ex.exercise_id);
                      return (
                        <div key={idx} className="bg-gray-900 p-3 rounded-md">
                          <p className="text-white font-medium">{exercise?.name || 'Nepoznata vežba'}</p>
                          <p className="text-gray-400 text-sm">
                            {ex.exercise_type === 'time' ? (
                              `Vreme: ${ex.time_duration}`
                            ) : (
                              `Serije: ${ex.sets && ex.sets.map((reps, i) => `${i + 1}×${reps}`).join(', ')}`
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return (
                <p className="mt-4 text-gray-400 text-sm text-center">
                  Dan {selectedDayForView} još nema dodeljenih vežbi
                </p>
              );
            })()}
          </div>

          <form onSubmit={handleAssignWorkout} className="space-y-4">
            <div>
              <Label htmlFor="day" className="text-gray-200">Dan za dodelu (1-5)</Label>
              <select id="day" name="day" required className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white" data-testid="workout-day-select">
                {[1, 2, 3, 4, 5].map(d => (
                  <option key={d} value={d}>Dan {d}</option>
                ))}
              </select>
            </div>

            {/* Dynamic exercise assignment */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="text-gray-200 text-base font-semibold">Dodeljene vežbe:</Label>
                <Button
                  type="button"
                  onClick={() => {
                    setSelectedExercises([...selectedExercises, { exercise_id: '', sets: [12, 10, 8] }]);
                  }}
                  className="accent-bg text-black font-semibold"
                  size="sm"
                >
                  <FaPlus className="mr-2" /> Dodaj vežbu
                </Button>
              </div>

              {selectedExercises.length === 0 ? (
                <div className="bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
                  <FaDumbbell className="text-4xl text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">Klikni "Dodaj vežbu" da dodaš prvu vežbu za ovaj dan</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedExercises.map((item, idx) => {
                    const exercise = exercises.find(e => e.id === item.exercise_id);
                    return (
                      <Card key={idx} className="bg-gray-800 border-gray-600">
                        <CardContent className="pt-6">
                          <div className="space-y-4">
                            {/* Exercise selector */}
                            <div className="flex gap-3 items-start">
                              <div className="flex-1">
                                <Label className="text-gray-200 mb-2 block">Vežba {idx + 1}</Label>
                                <select
                                  value={item.exercise_id}
                                  onChange={(e) => {
                                    const newSelected = [...selectedExercises];
                                    newSelected[idx].exercise_id = e.target.value;
                                    setSelectedExercises(newSelected);
                                  }}
                                  className="w-full bg-gray-900 border border-gray-600 rounded-md px-3 py-2 text-white"
                                  required
                                >
                                  <option value="">-- Izaberi vežbu --</option>
                                  {exercises.map(ex => (
                                    <option key={ex.id} value={ex.id}>
                                      {ex.name} ({ex.muscle_group})
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <Button
                                type="button"
                                onClick={() => {
                                  setSelectedExercises(selectedExercises.filter((_, i) => i !== idx));
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white mt-7"
                                size="sm"
                              >
                                <FaTrash />
                              </Button>
                            </div>

                            {/* Sets configuration */}
                            {item.exercise_id && (
                              <div className="space-y-3 pl-2 border-l-2 border-[#36e199]">
                                {/* Exercise Type Selection */}
                                <div>
                                  <Label className="text-gray-200 text-sm mb-2 block">Tip vežbe:</Label>
                                  <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`exercise-type-${idx}`}
                                        checked={exerciseTypes[item.exercise_id] !== 'time'}
                                        onChange={() => {
                                          const newTypes = {...exerciseTypes};
                                          newTypes[item.exercise_id] = 'reps';
                                          setExerciseTypes(newTypes);
                                          // Ensure sets array exists
                                          if (!item.sets || item.sets.length === 0) {
                                            const newSelected = [...selectedExercises];
                                            newSelected[idx].sets = [10];
                                            setSelectedExercises(newSelected);
                                          }
                                        }}
                                        className="accent-green-500"
                                      />
                                      <span className="text-white text-sm">Serije i ponavljanja</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`exercise-type-${idx}`}
                                        checked={exerciseTypes[item.exercise_id] === 'time'}
                                        onChange={() => {
                                          const newTypes = {...exerciseTypes};
                                          newTypes[item.exercise_id] = 'time';
                                          setExerciseTypes(newTypes);
                                        }}
                                        className="accent-green-500"
                                      />
                                      <span className="text-white text-sm">Vreme</span>
                                    </label>
                                  </div>
                                </div>

                                {/* Reps-based configuration */}
                                {exerciseTypes[item.exercise_id] !== 'time' && (
                                  <>
                                    <Label className="text-gray-200 text-sm">Serije i ponavljanja:</Label>
                                    <div className="grid grid-cols-3 gap-2">
                                      {item.sets.map((reps, setIdx) => (
                                        <div key={setIdx}>
                                          <Label className="text-gray-300 text-xs">Set {setIdx + 1}</Label>
                                          <Input
                                            type="number"
                                            value={reps}
                                            onChange={(e) => {
                                              const newSelected = [...selectedExercises];
                                              newSelected[idx].sets[setIdx] = parseInt(e.target.value) || 0;
                                              setSelectedExercises(newSelected);
                                            }}
                                            placeholder="Reps"
                                            className="bg-gray-900 border-gray-600 text-white"
                                            min="1"
                                            required
                                          />
                                        </div>
                                      ))}
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                      <Button
                                        type="button"
                                        onClick={() => {
                                          const newSelected = [...selectedExercises];
                                          newSelected[idx].sets.push(10);
                                          setSelectedExercises(newSelected);
                                        }}
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                        size="sm"
                                      >
                                        + Dodaj seriju
                                      </Button>
                                      {item.sets.length > 1 && (
                                        <Button
                                          type="button"
                                          onClick={() => {
                                            const newSelected = [...selectedExercises];
                                            newSelected[idx].sets.pop();
                                            setSelectedExercises(newSelected);
                                          }}
                                          className="bg-red-600 hover:bg-red-700 text-white text-xs"
                                          size="sm"
                                        >
                                          - Ukloni seriju
                                        </Button>
                                      )}
                                    </div>
                                  </>
                                )}

                                {/* Time-based configuration */}
                                {exerciseTypes[item.exercise_id] === 'time' && (
                                  <div>
                                    <Label className="text-gray-200 text-sm mb-2 block">Vreme trajanja:</Label>
                                    <Input
                                      type="text"
                                      placeholder="npr: 30s, 1m, 45s"
                                      value={item.time_duration || ''}
                                      onChange={(e) => {
                                        const newSelected = [...selectedExercises];
                                        newSelected[idx].time_duration = e.target.value;
                                        newSelected[idx].exercise_type = 'time';
                                        setSelectedExercises(newSelected);
                                      }}
                                      className="bg-gray-900 border-gray-600 text-white"
                                      required
                                    />
                                    <p className="text-gray-400 text-xs mt-1">Unesi vreme (npr: 30s za 30 sekundi, 1m za 1 minut)</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            <Button type="submit" disabled={selectedExercises.length === 0} className="w-full accent-bg text-black font-bold disabled:opacity-50" data-testid="submit-workout-btn">
              Dodeli trening
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set Nutrition Dialog */}
      <Dialog open={showSetNutrition} onOpenChange={setShowSetNutrition}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="set-nutrition-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Postavi nutritivne ciljeve - {selectedClient?.first_name} {selectedClient?.last_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSetNutrition} className="space-y-4">
            <div>
              <Label htmlFor="nutrition-day" className="text-gray-200">Dan (1-5)</Label>
              <select id="nutrition-day" name="day" required className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white" data-testid="nutrition-day-select">
                {[1, 2, 3, 4, 5].map(d => (
                  <option key={d} value={d}>Dan {d}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="calories" className="text-gray-200">Kalorije</Label>
                <Input id="calories" name="calories" type="number" step="0.1" required className="bg-gray-800 border-gray-600 text-white" data-testid="nutrition-calories-input" />
              </div>
              <div>
                <Label htmlFor="protein" className="text-gray-200">Proteini (g)</Label>
                <Input id="protein" name="protein" type="number" step="0.1" required className="bg-gray-800 border-gray-600 text-white" data-testid="nutrition-protein-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fats" className="text-gray-200">Masti (g)</Label>
                <Input id="fats" name="fats" type="number" step="0.1" required className="bg-gray-800 border-gray-600 text-white" data-testid="nutrition-fats-input" />
              </div>
              <div>
                <Label htmlFor="carbs" className="text-gray-200">Ugljeni hidrati (g)</Label>
                <Input id="carbs" name="carbs" type="number" step="0.1" required className="bg-gray-800 border-gray-600 text-white" data-testid="nutrition-carbs-input" />
              </div>
            </div>
            <Button type="submit" className="w-full accent-bg text-black font-bold" data-testid="submit-nutrition-btn">Postavi ciljeve</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Meal Plan Dialog */}
      <Dialog open={showMealPlan} onOpenChange={(open) => {
        setShowMealPlan(open);
        if (!open) {
          setSelectedMealDay(1);
        }
      }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="meal-plan-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Plan ishrane - {selectedClient?.first_name} {selectedClient?.last_name}</DialogTitle>
          </DialogHeader>
          
          {/* Current meal plans overview */}
          <div className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-700">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <FaUtensils /> Planovi po danima
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {[1, 2, 3, 4, 5, 6, 7].map(day => {
                const dayPlan = mealPlans.find(p => p.day === day);
                const hasPlan = dayPlan && dayPlan.meals.length > 0;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedMealDay(day)}
                    className={`p-2 rounded-lg text-center transition-all text-xs ${
                      selectedMealDay === day 
                        ? 'accent-bg text-black font-bold' 
                        : hasPlan 
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <div className="text-xs mb-1">Dan</div>
                    <div className="text-lg font-bold">{day}</div>
                    <div className="text-xs">{hasPlan ? dayPlan.meals.length : 0}</div>
                  </button>
                );
              })}
            </div>
            
            {/* Show meals for selected day */}
            {(() => {
              const dayPlan = mealPlans.find(p => p.day === selectedMealDay);
              if (dayPlan && dayPlan.meals.length > 0) {
                return (
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-white font-medium">Obroci za Dan {selectedMealDay}:</h4>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={() => {
                            setEditingMealPlan(dayPlan);
                            // Pre-fill form with existing data
                            document.getElementById('meal-day').value = dayPlan.day;
                            dayPlan.meals.forEach(meal => {
                              const input = document.getElementById(meal.meal_type);
                              if (input) input.value = meal.description;
                            });
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                          size="sm"
                        >
                          Uredi
                        </Button>
                        <Button
                          type="button"
                          onClick={() => handleDeleteMealPlan(dayPlan.id)}
                          className="bg-red-600 hover:bg-red-700 text-white text-sm"
                          size="sm"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                    {dayPlan.meals.map((meal, idx) => {
                      const mealTypeLabels = {
                        'dorucak': 'Doručak',
                        'rucak': 'Ručak',
                        'vecera': 'Večera',
                        'uzina': 'Užina'
                      };
                      return (
                        <div key={idx} className="bg-gray-900 p-3 rounded-md">
                          <p className="text-white font-medium">{mealTypeLabels[meal.meal_type] || meal.meal_type}</p>
                          <p className="text-gray-400 text-sm">{meal.description}</p>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return (
                <p className="mt-4 text-gray-400 text-sm text-center">
                  Dan {selectedMealDay} još nema plana ishrane
                </p>
              );
            })()}
          </div>

          <form onSubmit={handleCreateMealPlan} className="space-y-4">
            <div>
              <Label htmlFor="meal-day" className="text-gray-200">Dan (1-7)</Label>
              <select id="meal-day" name="day" required className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white">
                {[1, 2, 3, 4, 5, 6, 7].map(d => (
                  <option key={d} value={d}>Dan {d}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-3">
              <h4 className="text-white font-semibold">Obroci:</h4>
              
              <div>
                <Label htmlFor="dorucak" className="text-gray-200">Doručak</Label>
                <Textarea 
                  id="dorucak" 
                  name="dorucak" 
                  placeholder="Npr: Ovsena kaša sa voćem, jaja, puter..."
                  className="bg-gray-800 border-gray-600 text-white" 
                  rows={2}
                />
              </div>
              
              <div>
                <Label htmlFor="rucak" className="text-gray-200">Ručak</Label>
                <Textarea 
                  id="rucak" 
                  name="rucak" 
                  placeholder="Npr: Piletina sa povrćem, pirinač..."
                  className="bg-gray-800 border-gray-600 text-white" 
                  rows={2}
                />
              </div>
              
              <div>
                <Label htmlFor="vecera" className="text-gray-200">Večera</Label>
                <Textarea 
                  id="vecera" 
                  name="vecera" 
                  placeholder="Npr: Riba sa salatom, krompir..."
                  className="bg-gray-800 border-gray-600 text-white" 
                  rows={2}
                />
              </div>
              
              <div>
                <Label htmlFor="uzina" className="text-gray-200">Užina (opciono)</Label>
                <Textarea 
                  id="uzina" 
                  name="uzina" 
                  placeholder="Npr: Protein shake, voće, orašasti plodovi..."
                  className="bg-gray-800 border-gray-600 text-white" 
                  rows={2}
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              {editingMealPlan && (
                <Button 
                  type="button"
                  onClick={() => {
                    setEditingMealPlan(null);
                    document.querySelectorAll('textarea').forEach(ta => ta.value = '');
                  }}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold"
                >
                  Otkaži
                </Button>
              )}
              <Button type="submit" className="flex-1 accent-bg text-black font-bold">
                {editingMealPlan ? 'Ažuriraj plan obroka' : 'Kreiraj plan obroka'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Statistics Dialog */}
      <Dialog open={showClientStats} onOpenChange={setShowClientStats}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-stats-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">Statistika - {selectedClient?.first_name} {selectedClient?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Osnovni podaci</CardTitle>
              </CardHeader>
              <CardContent className="text-base">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-gray-300 font-medium">Visina:</span> <span className="text-white">{selectedClient?.height} cm</span>
                  </div>
                  <div>
                    <span className="text-gray-300 font-medium">Trenutna težina:</span> <span className="text-white">{selectedClient?.weight} kg</span>
                  </div>
                  <div>
                    <span className="text-gray-300 font-medium">Pol:</span> <span className="text-white">{selectedClient?.gender === 'male' ? 'Muškarac' : 'Žena'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {clientMeasurements.length > 0 ? (
              <>
                <h3 className="text-xl font-bold text-white mb-4">Grafički prikaz progresa</h3>
                
                {/* Weight Chart */}
                <Card className="bg-gray-800 border-gray-700 mb-6">
                  <CardHeader>
                    <CardTitle className="text-white">Težina kroz vreme</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={clientMeasurements.map(m => ({
                        date: new Date(m.week_date).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }),
                        weight: m.weight
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9CA3AF" />
                        <YAxis stroke="#9CA3AF" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                          labelStyle={{ color: '#F3F4F6' }}
                          itemStyle={{ color: '#36e199' }}
                        />
                        <Legend wrapperStyle={{ color: '#F3F4F6' }} />
                        <Line type="monotone" dataKey="weight" stroke="#36e199" strokeWidth={3} name="Težina (kg)" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Body Measurements Chart */}
                {clientMeasurements.some(m => m.waist || m.chest || m.thighs || m.arms || m.hips) && (
                  <Card className="bg-gray-800 border-gray-700 mb-6">
                    <CardHeader>
                      <CardTitle className="text-white">Telesna merenja kroz vreme</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={clientMeasurements.map(m => ({
                          date: new Date(m.week_date).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }),
                          waist: m.waist,
                          chest: m.chest,
                          thighs: m.thighs,
                          arms: m.arms,
                          hips: m.hips
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="date" stroke="#9CA3AF" />
                          <YAxis stroke="#9CA3AF" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                            labelStyle={{ color: '#F3F4F6' }}
                            itemStyle={{ color: '#36e199' }}
                          />
                          <Legend wrapperStyle={{ color: '#F3F4F6' }} />
                          {clientMeasurements.some(m => m.waist) && <Line type="monotone" dataKey="waist" stroke="#36e199" strokeWidth={2} name="Struk (cm)" />}
                          {clientMeasurements.some(m => m.chest) && <Line type="monotone" dataKey="chest" stroke="#3b82f6" strokeWidth={2} name="Grudi (cm)" />}
                          {clientMeasurements.some(m => m.thighs) && <Line type="monotone" dataKey="thighs" stroke="#f59e0b" strokeWidth={2} name="Butine (cm)" />}
                          {clientMeasurements.some(m => m.arms) && <Line type="monotone" dataKey="arms" stroke="#ef4444" strokeWidth={2} name="Ruke (cm)" />}
                          {clientMeasurements.some(m => m.hips) && <Line type="monotone" dataKey="hips" stroke="#8b5cf6" strokeWidth={2} name="Bokovi (cm)" />}
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                <h3 className="text-xl font-bold text-white mb-4">Istorija merenja</h3>
                <div className="space-y-3">
                  {clientMeasurements.map((m, idx) => (
                    <Card key={idx} className="bg-gray-800 border-gray-700">
                      <CardHeader>
                        <CardTitle className="text-white text-lg">
                          {new Date(m.week_date).toLocaleDateString('sr-RS')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-3 text-base">
                          <div>
                            <span className="text-gray-300 font-medium">Težina:</span>
                            <p className="text-xl font-bold text-white">{m.weight} kg</p>
                          </div>
                          {m.waist && (
                            <div>
                              <span className="text-gray-300 font-medium">Struk:</span>
                              <p className="text-xl font-bold text-white">{m.waist} cm</p>
                            </div>
                          )}
                          {m.thighs && (
                            <div>
                              <span className="text-gray-300 font-medium">Butine:</span>
                              <p className="text-xl font-bold text-white">{m.thighs} cm</p>
                            </div>
                          )}
                          {m.chest && (
                            <div>
                              <span className="text-gray-300 font-medium">Grudi:</span>
                              <p className="text-xl font-bold text-white">{m.chest} cm</p>
                            </div>
                          )}
                          {m.arms && (
                            <div>
                              <span className="text-gray-300 font-medium">Ruke:</span>
                              <p className="text-xl font-bold text-white">{m.arms} cm</p>
                            </div>
                          )}
                          {m.hips && (
                            <div>
                              <span className="text-gray-300 font-medium">Bokovi:</span>
                              <p className="text-xl font-bold text-white">{m.hips} cm</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-8 text-center">
                  <p className="text-gray-300 text-lg">Klijent još nije uneo merenja</p>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerDashboard;
